/**
 * Shared type contracts for the Kia Assistant SDK.
 * Kept domain-agnostic: no business/vertical concepts belong here.
 */
import type { AudioOutputProvider } from './audio/AudioOutput';
import type { LocationProvider } from './location/LocationProvider';
import type { MapData, WeatherDisplayData } from './output/renderers/types';

export type ElementRef = string | HTMLElement;

export interface KiaAssistantConfig {
  micElement: ElementRef;
  outputElement: ElementRef;
  playElement: ElementRef;
  /**
   * Base URL of our backend conversation API (never Gemini directly — the
   * browser SDK never talks to Gemini or holds a Gemini credential).
   * Defaults to '/api' for same-origin deployments.
   */
  apiBaseUrl?: string;
  /**
   * An explicit, host-supplied location. Takes precedence over anything
   * locationProvider would resolve (GPS/IP) — set this if the host page
   * already knows the user's location by whatever means.
   */
  location?: LocationContext;
  /**
   * Resolves a location per conversation turn when `location` above isn't
   * set. Defaults to a one-shot browser GPS reading (never continuous
   * tracking); pass `null` to disable location resolution entirely, or a
   * custom LocationProvider to replace the whole thing. There is no
   * IP-based fallback — if GPS is denied/unavailable, no location is sent,
   * and the assistant asks the user for it directly if a location-dependent
   * question comes up.
   */
  locationProvider?: LocationProvider | null;
  /**
   * Optional TTS provider override — defaults to the browser-native
   * SpeechSynthesis provider. Lets a host swap in a different
   * AudioOutputProvider (e.g. a cloud TTS provider later) without any
   * change to the SDK itself.
   */
  audioOutputProvider?: AudioOutputProvider;
  onStateChange?: (state: AssistantState) => void;
  onError?: (error: KiaAssistantError) => void;
  /**
   * Fired once, right after the microphone permission prompt (triggered by
   * open()) has been requested and settled — before geolocation is even
   * requested. Lets a publisher's creative sequence its own flow around the
   * two permission prompts instead of guessing when each one resolves.
   */
  onMicrophonePermission?: (result: PermissionOutcome) => void;
  /**
   * Fired once geolocation permission has been requested and settled —
   * always after onMicrophonePermission, since the SDK requests these
   * strictly in order (microphone, then location). Not fired at all if
   * location resolution is disabled (locationProvider: null) or an explicit
   * config.location was already supplied (no prompt is ever shown then).
   */
  onLocationPermission?: (result: PermissionOutcome) => void;
  /** Fired once a map widget has been rendered into the output element, with the data it was rendered from. */
  onMapRender?: (data: MapData) => void;
  /** Fired once a weather widget has been rendered into the output element, with the data it was rendered from. */
  onWeatherRender?: (data: WeatherDisplayData) => void;
  /**
   * Fired once, right after voice output finishes for the response that
   * reached this session's per-session question limit — i.e. right after
   * the user hears the last real answer they'll get this session. Nothing
   * further is closed/transitioned automatically; a publisher's own handler
   * is expected to call close()/destroy() and swap to whatever comes next
   * in their own creative.
   */
  onSessionLimitReached?: () => void;
}

/** `granted` reflects whether the capability actually became available — a denial and an unsupported/unavailable environment both come through as `false`, indistinguishably (the underlying browser APIs don't otherwise let us tell those apart). */
export interface PermissionOutcome {
  granted: boolean;
}

/**
 * How a location was obtained and how much to trust it. `source` must be
 * preserved end to end — an IP-derived guess must never be presented or
 * treated as precise GPS.
 */
export type LocationSource = 'explicit' | 'gps' | 'ip';
export type LocationPrecision = 'precise' | 'approximate';

export interface LocationContext {
  latitude: number;
  longitude: number;
  source: LocationSource;
  precision: LocationPrecision;
  /** Human-readable locality (e.g. "Mumbai, Maharashtra, India") — currently only ever populated for IP-derived locations. Informational only, never used for coordinate math. */
  label?: string;
}

export type AssistantState =
  'IDLE' | 'INITIALIZING' | 'READY' | 'LISTENING' | 'PROCESSING' | 'SPEAKING' | 'ERROR';

export class KiaAssistantError extends Error {
  constructor(
    message: string,
    public readonly code: string,
  ) {
    super(message);
    this.name = 'KiaAssistantError';
  }
}

/** What UI shape the renderer should use for a given assistant response — dispatched in OutputRenderer.render(). */
export type DisplayType = 'text' | 'map' | 'weather' | 'mixed';

/**
 * The normalized, backend-validated contract for a Gemini conversation turn.
 * - transcript is Gemini's own understanding of what the user said, shown by
 *   OutputRenderer ahead of the response so the exchange reads as a
 *   conversation. Never spoken by TTS — only message.text is.
 * - message.text is the canonical human-readable response (also what
 *   AudioOutput.speak() will eventually receive).
 * - display.type tells the renderer what UI shape to use; the renderer (not
 *   Gemini) owns the actual DOM work.
 * - data carries whatever structured payload a future renderer needs.
 * - sessionLimitReached is server-derived (from the per-session question
 *   count), never something Gemini decides — true means this is the last
 *   real answer this session will ever get.
 */
export interface AssistantResponse {
  transcript: string;
  message: {
    text: string;
    /** BCP-47 tag (e.g. "en-US", "hi-IN") for the language `text` is written in — picks the TTS voice. */
    language: string;
  };
  display: {
    type: DisplayType;
  };
  data: Record<string, unknown>;
  sessionLimitReached: boolean;
}
